import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import { Puzzle, Sparkles } from 'lucide-react';

import { Box, Chip, Menu, Stack, Switch, MenuItem, Typography, IconButton, CircularProgress } from '@mui/material';

import {
  Iconify,
  Stagger,
  StaggerItem,
  DashboardCard,
  DashboardGradientDefs,
  MessagesSquareIcon,
  SkillTypeChip,
  ConnectorIcon,
  StatusChip,
  useChats,
  useDeleteChat,
  solidItem,
  solidIconButton,
  responsiveCardWidth,
  pressActiveSelector,
  motion as motionTokens,
} from '@nexus/ari-lib';

import { useStore } from '../../../lib/store';
import { useSkills } from '../../../hooks/use-skills';
import { useConnectors } from '../../../hooks/use-connectors';
import { DashboardMediaSection } from '../dashboard-media-section';
import { DashboardMemorySection } from '../dashboard-memory-section';
import { DashboardAccountSection } from '../dashboard-account-section';
import { DashboardDevicesSection } from '../dashboard-devices-section';
import { DashboardDocumentsSection } from '../dashboard-documents-section';

import type { ChatApiItem } from '@nexus/ari-lib';
import { useDevices } from '../../../hooks/use-devices';

// Rows keep their natural height and any leftover card space stays empty, so a
// card with one entry doesn't render a single stretched row.
const MAX_CARD_ROWS = 3;

// The Connectors rows next to this card stretch to 47.3px at their 4-row cap.
const SKILL_ROW_HEIGHT = 48;

function DashboardEmptyState({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: 1,
        color: 'text.secondary',
      }}
    >
      {icon}
      <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.4 }}>
        {label}
      </Typography>
    </Box>
  );
}

function DashboardChatRow({
  chat,
  onSelect,
  onDelete,
}: {
  chat: ChatApiItem;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onSelect(chat.id);
        }}
        sx={{
          px: 2,
          flex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          borderRadius: 1,
          '&:hover': { bgcolor: 'container.high' },
          // Row, not its "⋯" menu button — guard against inner-control presses.
          [pressActiveSelector]: { transform: reduceMotion ? 'none' : 'scale(0.98)' },
          transition: `background-color ${motionTokens.duration.fast} ${motionTokens.ease.out}, transform ${motionTokens.duration.instant} ${motionTokens.ease.out}`,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography fontWeight={700} noWrap sx={{ display: 'block', lineHeight: 1.4, fontSize: 12 }}>
            {chat.title || 'Untitled'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {chat.summary}
          </Typography>
        </Box>
        <IconButton
          aria-label={t('accessibility.chatOptions')}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchor(e.currentTarget);
          }}
          sx={{
            ml: 1,
            color: 'grey.50',
            ...solidIconButton,
            borderRadius: '50%',
            width: 24,
            height: 24,
          }}
        >
          <Iconify icon="solar:menu-dots-bold" width={14} />
        </IconButton>
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'container.medium',
              backgroundImage: 'none',
              border: '1px solid',
              borderColor: 'grey.800',
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onDelete(chat.id);
          }}
          sx={{ color: 'error.main', fontSize: 14 }}
        >
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

export function DashboardView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const supabaseUser = useStore((s) => s.supabaseUser);
  const { userDevices, error: userDevicesError, isLoading: userDevicesLoading } = useDevices();
  const userId = supabaseUser?.id || null;
  const itemStyle = solidItem;

  const { data, isLoading: isLoadingChats } = useChats({
    limit: 4,
    enabled: !!supabaseUser,
  });
  const { mutate: deleteChat } = useDeleteChat();
  const { skillsCatalog, userEnabledSkillIds, isLoading: isLoadingSkills, enableSkill, disableSkill } = useSkills();
  const {
    connectorsCatalog,
    userEnabledConnectorIds,
    connectedConnectorIds,
    isLoading: isLoadingConnectors,
  } = useConnectors();

  const recentChats = data?.chats ?? [];

  const isConnected = (c: typeof connectorsCatalog[number]) =>
    connectedConnectorIds.has(c.id) && userEnabledConnectorIds.has(c.id);

  const sortedConnectors = useMemo(
    () =>
      [...connectorsCatalog].sort((a, b) => {
        const aConn = isConnected(a) ? 0 : 1;
        const bConn = isConnected(b) ? 0 : 1;
        return aConn - bConn;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectorsCatalog, connectedConnectorIds, userEnabledConnectorIds]
  );

  return (
    <Box data-testid="cloud.dashboard.container">
      <DashboardGradientDefs />

      <Stagger
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 2.5,
        }}
      >
        {[
          <DashboardAccountSection
            name={supabaseUser?.name}
            email={supabaseUser?.email || ''}
            picture={supabaseUser?.picture}
            onClick={() => navigate({ to: '/account' })}
          />,
          <DashboardDevicesSection
            userDevices={userDevices}
            userDevicesError={userDevicesError}
            userDevicesLoading={userDevicesLoading}
            comingSoon={false}
            onClick={() => navigate({ to: '/devices' })}
          />,
        ].map((child, i) => (
          <StaggerItem
            key={i}
            sx={{
              ...responsiveCardWidth,
              height: 308,
            }}
          >
            {child}
          </StaggerItem>
        ))}

        <StaggerItem
          sx={{
            ...responsiveCardWidth,
            height: 308,
          }}
        >
          <DashboardCard
            data-testid="cloud.dashboard.connectors-section"
            title="Connectors"
            subtitle="Apps and services for Ari"
            icon={<Puzzle size={28} strokeWidth={1} />}
            onClick={() => navigate({ to: '/connectors' })}
          >
            {isLoadingConnectors ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : connectorsCatalog.length === 0 ? (
              <DashboardEmptyState
                icon={<Iconify icon="solar:widget-2-outline" width={40} sx={{ opacity: 0.4 }} />}
                label="No connectors available"
              />
            ) : (
              <Stack spacing={1} sx={{ flex: 1 }}>
                {sortedConnectors.slice(0, 4).map((connector) => {
                  const connected = isConnected(connector);
                  return (
                    <Box
                      key={connector.id}
                      sx={{
                        px: 1.5,
                        flex: 1,
                        ...itemStyle,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <ConnectorIcon connector={connector} />
                      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} noWrap>
                        {connector.name}
                      </Typography>
                      {connected ? (
                        <StatusChip color="success" label="Connected" />
                      ) : (
                        <Chip
                          label="Not connected"
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 600,
                            bgcolor: 'container.medium',
                            color: 'text.secondary',
                            borderRadius: '50px',
                            '& .MuiChip-label': { px: 1.25 },
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </DashboardCard>
        </StaggerItem>

        <StaggerItem
          sx={{
            ...responsiveCardWidth,
            height: 308,
          }}
        >
          <DashboardCard
            data-testid="cloud.dashboard.skills-section"
            title="Skills"
            subtitle="Extend what Ari can do"
            icon={
              <Box component="span" sx={{ '& circle': { display: 'none' } }}>
                <Sparkles size={28} strokeWidth={1} />
              </Box>
            }
            onClick={() => navigate({ to: '/skills' })}
          >
            {isLoadingSkills ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : skillsCatalog.length === 0 ? (
              <DashboardEmptyState
                icon={<Sparkles size={40} strokeWidth={1} style={{ opacity: 0.4 }} />}
                label="No skills configured"
              />
            ) : (
              <Stack spacing={1} sx={{ flex: 1 }}>
                {skillsCatalog.slice(0, MAX_CARD_ROWS).map((skill) => {
                  const isEnabled = userEnabledSkillIds.has(skill.id);
                  return (
                    <Box
                      key={skill.id}
                      sx={{
                        px: 1.5,
                        height: SKILL_ROW_HEIGHT,
                        ...itemStyle,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} noWrap>
                        {skill.name}
                      </Typography>
                      <SkillTypeChip isCatalog={!skill.created_by} size="sm" />
                      <Switch
                        slotProps={{
                          input: { role: 'switch', 'aria-label': t('accessibility.toggleSkill', { name: skill.name }) },
                        }}
                        checked={isEnabled}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => (isEnabled ? disableSkill(skill.id) : enableSkill(skill.id))}
                        sx={{ ml: -0.5 }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            )}
          </DashboardCard>
        </StaggerItem>

        <StaggerItem
          sx={{
            ...responsiveCardWidth,
            height: 308,
          }}
        >
          <DashboardMemorySection userId={userId} onClick={() => navigate({ to: '/memory' })} />
        </StaggerItem>

        <StaggerItem
          sx={{
            ...responsiveCardWidth,
            height: 308,
          }}
        >
          <DashboardCard
            data-testid="cloud.dashboard.chats-section"
            title="Chats"
            subtitle="Recent Chats"
            icon={<MessagesSquareIcon sx={{ width: 28, height: 28 }} />}
            onClick={() => navigate({ to: '/chats' })}
          >
            {isLoadingChats ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : recentChats.length === 0 ? (
              <DashboardEmptyState
                icon={<MessagesSquareIcon sx={{ width: 40, height: 40, opacity: 0.4 }} />}
                label="No chats yet"
              />
            ) : (
              <Box
                sx={{
                  ...solidItem,
                  borderRadius: 2,
                  overflow: 'hidden',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {recentChats.slice(0, 4).map((chat, index, arr) => (
                  <Box key={chat.id} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <DashboardChatRow
                      chat={chat}
                      onSelect={(id) =>
                        navigate({
                          to: '/chats/$chatId',
                          params: { chatId: id },
                        })
                      }
                      onDelete={(id) => deleteChat(id)}
                    />
                    {index < arr.length - 1 && (
                      <Box
                        sx={{
                          mx: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </DashboardCard>
        </StaggerItem>

        <StaggerItem
          sx={{
            ...responsiveCardWidth,
            height: 308,
          }}
        >
          <DashboardMediaSection items={[]} comingSoon />
        </StaggerItem>

        <StaggerItem
          sx={{
            ...responsiveCardWidth,
            height: 308,
          }}
        >
          <DashboardDocumentsSection documents={[]} comingSoon />
        </StaggerItem>
      </Stagger>
    </Box>
  );
}
